// Copyright (c) Docugami, Inc. All rights reserved.

import { Modal } from "@fluentui/react";
import { dgFontFamily } from "components/common/Fonts";
import { greyText, modalShadow, primaryThemeSettings } from "components/shell/presentation/AppStyles";
import { useStorageProviderToken } from "hooks/StorageProviderToken";
import { ExportConfiguration, ExportConfigurationStatus, WorkPathType } from "networking/WorkPathApi";
import { useMemo } from "react";
import { useAddExportConfigurationMutation, useDeleteExportConfigurationMutation, useUpdateExportConfigurationStatusMutation } from "store/work_path/workPathSlice";
import styled, { css } from "styled-components";
import { ExportConfigurationView } from "./ExportConfigurationView";
import { ExportDestination, ExportDestinationPicker } from "./ExportDestinationPicker";

const DisabledCss = css`
    opacity: .5;
    cursor: wait;
    button {
        pointer-events: none;
    }
`

const ModalContainer = styled(Modal) <{ disabled?: boolean }>`
    .ms-Dialog-main {
        border-radius: 20px;
        box-shadow: ${modalShadow};
        width: 75vw;
        height: 75vh;
        overflow: hidden;
    }
    .ms-Modal-scrollableContent {
        width: 100%;
        height: 100%;
        box-sizing: border-box;
        padding: 34px 34px 20px 34px;
        background-color: ${primaryThemeSettings.palette.white};
        display: flex;
        flex-direction: column;
        ${props => props.disabled && DisabledCss}
    }
`;

const Title = styled.span`
    border-bottom: 1px solid ${greyText};
    padding: 8px 0px 27px 0px;
    font-family: ${dgFontFamily};
    font-weight: 300;
    font-size: 36px;
    line-height: 44px;
    width: 100%;
`;

interface WorkPathExportConfigurationModalProps {
    isModalOpen: boolean;
    onClose: () => void;
    workPathId: string;
    configuration?: ExportConfiguration;
    title: string;
    workPathType: WorkPathType;
}

export const WorkPathExportConfigurationModal = (props: WorkPathExportConfigurationModalProps): JSX.Element => {
    const { getToken, statusModal } = useStorageProviderToken(props.configuration?.providerId ?? null);

    const [triggerAddConfig, { isLoading: addConfigInProgress }] = useAddExportConfigurationMutation();
    const [triggerUpdateConfig, { isLoading: updateConfigInProgress }] = useUpdateExportConfigurationStatusMutation();
    const [triggerDeleteConfig, { isLoading: deleteConfigInProgress }] = useDeleteExportConfigurationMutation();

    function onClose() {
        props.onClose();
    }

    async function onDestinationPicked(target: ExportDestination) {
        await triggerAddConfig({
            workPathId: props.workPathId,
            providerId: target.providerId,
            destination: target.item
        })
    }

    async function toggleEnabled() {
        if (!props.configuration) {
            throw new Error("Could not find a valid configuration.")
        }

        let isDisabled = props.configuration.isDisabled;
        if (props.configuration.status === ExportConfigurationStatus.Ready) {
            // The config is valid so we are toggling the value
            isDisabled = !isDisabled;
        }
        else {
            // Get a new token to ensure we have a valid connection
            const token = await getToken();
            if (!token)
            {
                throw new Error("Could not get a valid token to reconnect export config.")
            }
        }

        await triggerUpdateConfig({
            workPathId: props.workPathId,
            configurationId: props.configuration!.id, 
            disabled: isDisabled
        });
    }

    async function onDeleteConfiguration() {
        if (isDisabled || !props.configuration) {
            return;
        }

        await triggerDeleteConfig({
            workPathId: props.workPathId,
            configurationId: props.configuration.id
        })
    }

    const isDisabled = useMemo(() => {
        return addConfigInProgress || deleteConfigInProgress || updateConfigInProgress;
    }, [addConfigInProgress, deleteConfigInProgress, updateConfigInProgress]);

    return (
        <ModalContainer
            isOpen={props.isModalOpen}
            onDismiss={onClose}
            isBlocking={false}
            disabled={isDisabled}>
            <Title>{`${props.title} Export`}</Title>
            {statusModal}
            {!props.configuration && <ExportDestinationPicker onSelected={onDestinationPicked} />}
            {!!props.configuration &&
                <ExportConfigurationView
                    configuration={props.configuration}
                    workPathType={props.workPathType}
                    workPathId={props.workPathId}
                    title={props.title}
                    onClose={onClose}
                    onDelete={onDeleteConfiguration}
                    onToggleEnabled={toggleEnabled}
                    updatingStatus={updateConfigInProgress}/>
            }
        </ModalContainer>
    );
}






